import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import {
  Add as NewChatIcon,
  DeleteOutline as EndChatIcon,
} from '@mui/icons-material';
import type { SessionListItem } from './types';

interface SessionListProps {
  sessions: SessionListItem[];
  activeSessionId: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  closingSessionId: string | null;
  onChange: (sessionId: string) => void;
  onCreate: () => void;
  onClose: (sessionId: string) => void;
}

function formatSessionLabel(session: SessionListItem, index: number): string {
  return session.title ?? `Chat ${index + 1}`;
}

export const SessionList = ({
  sessions,
  activeSessionId,
  isLoading,
  isStreaming,
  closingSessionId,
  onChange,
  onCreate,
  onClose,
}: SessionListProps) => {
  const selectedSessionId =
    activeSessionId && sessions.some((session) => session.id === activeSessionId)
      ? activeSessionId
      : '';
  const activeSession = sessions.find((session) => session.id === selectedSessionId) ?? null;
  const isClosingActiveSession = activeSession?.id === closingSessionId;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <Select
          displayEmpty
          value={selectedSessionId}
          onChange={(event) => onChange(String(event.target.value))}
          disabled={isLoading || isStreaming || Boolean(closingSessionId)}
          sx={{ fontSize: 12, height: 32 }}
          renderValue={(value) => {
            if (!value) {
              return isLoading ? 'Loading chats...' : 'Select chat';
            }

            const index = sessions.findIndex((session) => session.id === value);
            return index >= 0 ? formatSessionLabel(sessions[index], index) : 'Select chat';
          }}
        >
          {sessions.map((session, index) => (
            <MenuItem key={session.id} value={session.id} sx={{ fontSize: 12 }}>
              {formatSessionLabel(session, index)} ({session.messageCount})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip title="New chat">
        <span>
          <IconButton
            size="small"
            onClick={onCreate}
            disabled={isStreaming || Boolean(closingSessionId)}
          >
            <NewChatIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={
          isClosingActiveSession
            ? 'Ending conversation...'
            : activeSession
              ? 'End conversation'
              : 'No active conversation'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => activeSession && onClose(activeSession.id)}
            disabled={!activeSession || isStreaming || Boolean(closingSessionId)}
          >
            {isClosingActiveSession ? <CircularProgress size={18} /> : <EndChatIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};